import { getThreadScorerIdsForAgent } from '@fruitful/llm-service-shared';
import type { ThreadError } from '@fruitful/types';
import type { ClientScoreRowData } from '@mastra/client-js';

import { requestBuilder } from '../../http/RequestBuilder';
import { formatScorerValue, scoresFromLatestRun } from '../utils/scoreUtils';

export interface EvalFeedbackScoreSnapshot {
    scorerId: string;
    value: string;
    reason?: string;
}

export interface EvalFeedbackErrorSnapshot {
    type: string;
    message?: string;
}

export interface CreateEvalFeedbackBody {
    title: string;
    description: string;
    threadId: string;
    agentId: string;
    assignToBot: boolean;
    scores?: EvalFeedbackScoreSnapshot[];
    errors?: EvalFeedbackErrorSnapshot[];
}

export interface CreateEvalFeedbackResponse {
    id: string;
    identifier: string;
    url: string;
}

export const toEvalFeedbackScores = (
    scores: ClientScoreRowData[],
    agentId: string,
): EvalFeedbackScoreSnapshot[] => {
    return scoresFromLatestRun(scores, getThreadScorerIdsForAgent(agentId)).map(
        (score) => {
            const reason = score.reason?.trim();

            return {
                scorerId: score.scorerId,
                value: formatScorerValue({
                    score: score.score,
                    scorerId: score.scorerId,
                }),
                ...(reason != null && reason !== '' ? { reason } : {}),
            };
        },
    );
};

export const toEvalFeedbackErrors = (
    errors: ThreadError[],
): EvalFeedbackErrorSnapshot[] => {
    return errors.map((error) => {
        switch (error.type) {
            case 'tool':
                return { type: error.type, message: error.name };
            case 'stream':
            case 'noReply':
            case 'emptyReply':
            case 'slowReply':
            case 'nudge':
            case 'loop':
            case 'multipleReplies':
            case 'moneyUnit':
                return { type: error.type };
            default: {
                const _exhaustive: never = error;
                return _exhaustive;
            }
        }
    });
};

const useCreateThreadFeedbackMutation = requestBuilder.mutation<
    CreateEvalFeedbackResponse,
    CreateEvalFeedbackBody
>({
    endpoint: '/api/admin/feedback',
    method: 'POST',
});

export const useCreateThreadFeedback = () => {
    return useCreateThreadFeedbackMutation();
};
